import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import pdfDataCache from '../documents/pdfDataCache';

// Types
export const SampleType = {
  id: '',
  gml_id: '',
  name: '',
  starttime: null,
  endtime: null,
  lowerdepth: null,
  lowerdepth_uom: null,
  lowerdepth_condition: null,
  upperdepth: null,
  upperdepth_uom: null,
  upperdepth_condition: null,
  materialclass: null,
  samplingtime: null,
  specimentype: null,
  borehole_id: null,
  created_at: null,
  updated_at: null,
  sample_id: null, //Mengmonsters
  document_id: '',
  type: null,
};

// Hook for fetching samples
export const useSamples = (param = {}) => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define the core fetching logic using useCallback
  const fetchSamplesInternal = useCallback(async () => {
    let sampleType = 'all'; // Gebruik var ipv let
    try {
      setLoading(true);
      setError(null);

      const documentId = param.document_id || 'global';
      sampleType = param.type || 'all'; // Wijs waarde toe binnen try
      const tabName = sampleType === 'all' ? 'samples' : `samples-${sampleType}`;
      const extraKey = JSON.stringify(param);
      const cachedData = pdfDataCache.get(documentId, tabName, extraKey);

      if (cachedData) {
        setSamples(cachedData);
        setLoading(false);
        return; // Exit if using cache
      }

      // Build the query
      let query = supabase
        .from('document_sample')
        .select(`
          *,
          lowerdepth_condition:document_domainvalueslookup!document_sample_lowerdepth_condition_fkey(*),
          lowerdepth_uom:document_domainvalueslookup!document_sample_lowerdepth_uom_fkey(*),
          upperdepth_condition:document_domainvalueslookup!document_sample_upperdepth_condition_fkey(*),
          upperdepth_uom:document_domainvalueslookup!document_sample_upperdepth_uom_fkey(*),
          materialclass:document_domainvalueslookup!document_sample_materialclass_fkey(*)
        `);

      // Apply standard filters
      if (param.borehole_id) query = query.eq('borehole_id', param.borehole_id);
      if (param.document_id) query = query.eq('document_id', param.document_id);
      if (param.materialclass) query = query.eq('materialclass', param.materialclass);
      if (param.specimentype) query = query.eq('specimentype', param.specimentype);

      // Filter op de 'type' kolom als deze is opgegeven
      if (sampleType !== 'all') {
        query = query.eq('type', sampleType);
      }

      // Apply custom filters
      if (param.filters && Array.isArray(param.filters)) {
        for (const filter of param.filters) {
          if (filter.operator === 'eq') query = query.eq(filter.column, filter.value);
          else if (filter.operator === 'is') query = query.is(filter.column, filter.value);
        }
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;

      pdfDataCache.set(documentId, tabName, data, extraKey);
      setSamples(data);

    } catch (err) {
      // Nu is sampleType hier gegarandeerd beschikbaar
      // Log de waarde om te debuggen
      console.log('Error caught, sampleType is:', sampleType);
      
      // Gebruik een fallback voor de zekerheid
      const typeForError = sampleType || 'unknown type';
      
      console.error(`Error fetching ${typeForError} samples:`, err);
      setError(err.message || `Fout bij ophalen ${typeForError} samples`);
    } finally {
      setLoading(false);
    }
  }, [param]); // Dependency array includes param

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates on unmounted component

    // Initial fetch using the useCallback function
    fetchSamplesInternal();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        fetchSamplesInternal();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSamplesInternal, param.type]); // Add param.type dependency

  // Return the refetch function along with state
  return { samples, loading, error, refetch: fetchSamplesInternal };
};

// New Hook specifically for fetching samples with analyse_monster = true
export const useAnalysisSamples = (param = {}) => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchAnalysisSamples = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);
        setError(null);

        // Cache keys
        const documentId = param.document_id || 'global-analysis';
        const tabName = 'analysis-samples';
        const extraKey = JSON.stringify(param);
        
        const cachedData = pdfDataCache.get(documentId, tabName, extraKey);
        
        if (cachedData) {
          if (isMounted) {
              setSamples(cachedData);
              setLoading(false);
          }
          return;
        }

        let query = supabase
          .from('document_sample')
          .select(`
            *,
            materialclass:document_domainvalueslookup!document_sample_materialclass_fkey(description)
          `)
          .eq('type', 'analyse');


        if (param.document_id) {
          query = query.eq('document_id', param.document_id);
        }

        const { data, error: queryError } = await query;
        
        if (queryError) throw queryError;
        
        pdfDataCache.set(documentId, tabName, data, extraKey);
        
        if (isMounted) {
          setSamples(data);
        }
      } catch (err) {
        console.error("Error fetching analysis samples:", err);
        if (isMounted) {
          setError(err.message || 'Fout bij ophalen analyse monsters');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAnalysisSamples();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        fetchAnalysisSamples();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [param]);

  return { samples, loading, error }; // Return same structure as useSamples
};

// Helper function to fetch a single sample by ID
export const getSampleById = async (id) => {
  const cachedData = pdfDataCache.get('global', 'sample_by_id', id);
  if (cachedData) {
    return cachedData;
  }

  const { data, error } = await supabase
    .from('document_sample')
    .select(`
      *,
      lowerdepth_condition:document_domainvalueslookup!document_sample_lowerdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_sample_lowerdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_sample_upperdepth_condition_fkey(*),
      upperdepth_uom:document_domainvalueslookup!document_sample_upperdepth_uom_fkey(*),
      materialclass:document_domainvalueslookup!document_sample_materialclass_fkey(*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  
  pdfDataCache.set('global', 'sample_by_id', data, id);
  
  return data;
};

// Helper function to create a new sample
export const createSample = async (sampleData) => {
  // Zorg ervoor dat het type correct is ingesteld (of verwijder oude 'mengmonster')
  const dataToInsert = { ...sampleData };
  delete dataToInsert.mengmonster; // Verwijder de oude kolom indien aanwezig
  if (!dataToInsert.type && sampleData.sample_id) {
      dataToInsert.type = 'veld'; // Default naar veld als er een parent is
  } else if (!dataToInsert.type) {
      dataToInsert.type = 'analyse'; // Default naar analyse anders
  }

  const { data, error } = await supabase
    .from('document_sample')
    .insert([dataToInsert]) // Gebruik de aangepaste data
    .select();

  if (error) throw error;

  if (dataToInsert.document_id) {
    // Invalideer algemene en type-specifieke cache
    invalidateSampleCache(dataToInsert.document_id);
  }
  // if (sampleData.borehole_id) {
  //   pdfDataCache.clearTab(sampleData.borehole_id, 'samples_by_borehole');
  // }

  return data?.[0]; // Return the first created item
};

// Helper function to update a sample
export const updateSample = async (id, updates) => {
  // Verwijder 'mengmonster' als het per ongeluk in updates zit
  const updatesToApply = { ...updates };
  delete updatesToApply.mengmonster;

  const { data, error } = await supabase
    .from('document_sample')
    .update(updatesToApply) // Gebruik aangepaste updates
    .eq('id', id)
    .select();

  if (error) throw error;

  // Invalideer caches na update
  const sample = data?.[0];
  if (sample?.document_id) {
    invalidateSampleCache(sample.document_id);
  }
  pdfDataCache.clearTab('global', 'sample_by_id', id); // Cache for specific sample

  return sample;
};

// Helper function to delete a sample
export const deleteSample = async (id) => {
  // Optioneel: Haal eerst document_id op als dat nodig is voor invalidatie
  const sampleToDelete = await getSampleById(id); 

  const { error } = await supabase
    .from('document_sample')
    .delete()
    .eq('id', id);

  if (error) throw error;

  // Invalideer caches na verwijderen
  if (sampleToDelete?.document_id) {
    invalidateSampleCache(sampleToDelete.document_id);
  }
  pdfDataCache.clearTab('global', 'sample_by_id', id);
};

// Helper function to get samples for a specific borehole
export const getSamplesByBoreholeId = async (boreholeId) => {
  const cachedData = pdfDataCache.get('global', 'samples_by_borehole_' + boreholeId, '');
  if (cachedData) {
    return cachedData;
  }
  
  const { data, error } = await supabase
    .from('document_sample')
    .select(`
      *,
      lowerdepth_condition:document_domainvalueslookup!document_sample_lowerdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_sample_lowerdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_sample_upperdepth_condition_fkey(*),
      upperdepth_uom:document_domainvalueslookup!document_sample_upperdepth_uom_fkey(*),
      materialclass:document_domainvalueslookup!document_sample_materialclass_fkey(*)
    `)
    .eq('borehole_id', boreholeId);

  if (error) throw error;
  
  pdfDataCache.set('global', 'samples_by_borehole_' + boreholeId, data, '');
  
  return data;
};

// Helper function to get samples by document id
export const getSamplesByDocumentId = async (documentId) => {
  const cachedData = pdfDataCache.get(documentId, 'samples', '');
  if (cachedData) {
    return cachedData;
  }
  
  const { data, error } = await supabase
    .from('document_sample')
    .select(`
      *,
      lowerdepth_condition:document_domainvalueslookup!document_sample_lowerdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_sample_lowerdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_sample_upperdepth_condition_fkey(*),
      upperdepth_uom:document_domainvalueslookup!document_sample_upperdepth_uom_fkey(*),
      materialclass:document_domainvalueslookup!document_sample_materialclass_fkey(*)
    `)
    .eq('document_id', documentId);

  if (error) throw error;
  
  pdfDataCache.set(documentId, 'samples', data, '');
  
  return data;
};

// Helper function to invalidate all sample caches
export const invalidateSampleCache = (documentId = null) => {
  if (documentId) {
    pdfDataCache.clearTab(documentId, 'samples'); // Algemene cache voor document
    pdfDataCache.clearTab(documentId, 'samples-veld'); // Specifieke cache
    pdfDataCache.clearTab(documentId, 'samples-analyse'); // Specifieke cache
    // Mogelijk ook andere gerelateerde caches hier clearen indien nodig
  }
  // Optioneel: globale caches clearen indien relevant
  // pdfDataCache.clearTab('global', 'samples');
};
